import { formErrorsFromFields, type FormErrors } from '@/lib/form-errors';
import { formatNumber } from './campaigns-format';
import {
  parseGastoField,
  type CampaignFormState,
  type GastoFormState,
} from './campaigns-payload';

/**
 * Validación del formulario en el cliente: espejo de `campaigns.schemas.ts` del backend.
 *
 * No pretende reemplazarlo (el backend valida lo mismo y más: unicidad del anuncio/campaña,
 * existencia del tenant), sino no gastar un viaje al servidor en lo evidente —nombre vacío, gasto
 * que no es un número, moneda que no es un código ISO— y dejar el mensaje debajo del campo.
 *
 * El caso propio de este módulo es el `refine` del schema: una fila necesita `metaAdId` o
 * `metaCampaignId`. Se valida acá porque es lo que el admin puede arreglar en el formulario.
 */

/** Topes del contrato (`campaigns.schemas.ts`). */
export const CAMPAIGN_LIMITS = {
  nombre: 150,
  /** `metaAdId` / `metaCampaignId`: `z.string().trim().min(1).max(120)`. */
  metaId: 120,
  /** `gastoActual`: `z.coerce.number().min(0).max(1_000_000_000)`. */
  gastoMax: 1_000_000_000,
} as const;

/** «El nombre es obligatorio» / «El nombre no puede pasar de 150 caracteres (tienes 162)». */
export function validateCampaignName(value: string): string | undefined {
  const text = value.trim();
  if (text.length === 0) return 'El nombre es obligatorio';
  if (text.length > CAMPAIGN_LIMITS.nombre) {
    return `El nombre no puede pasar de ${CAMPAIGN_LIMITS.nombre} caracteres (tienes ${text.length})`;
  }
  return undefined;
}

/**
 * Id de Meta: obligatorio donde toque y siempre ≤ 120 caracteres.
 *
 * `requerido` distingue las tres situaciones del contrato: el `metaAdId` de una fila por anuncio,
 * el `metaCampaignId` de una fila por campaña y el `metaCampaignId` opcional de una fila por
 * anuncio (que solo agrupa).
 */
export function validateMetaId(value: string, label: string, requerido: boolean): string | undefined {
  const text = value.trim();
  if (text.length === 0) return requerido ? `${label} es obligatorio` : undefined;
  if (text.length > CAMPAIGN_LIMITS.metaId) {
    return `${label} no puede pasar de ${CAMPAIGN_LIMITS.metaId} caracteres (tienes ${text.length})`;
  }
  return undefined;
}

/**
 * Gasto acumulado: vacío se acepta (en el alta el backend pone 0), pero si hay algo tiene que ser
 * un número ≥ 0 dentro del tope del contrato.
 */
export function validateGastoActual(value: string): string | undefined {
  const text = value.trim();
  if (text.length === 0) return undefined;

  const parsed = parseGastoField(text);
  if (parsed === null) {
    return 'El gasto tiene que ser un número (por ejemplo 25000 o 25000,50)';
  }
  if (parsed < 0) return 'El gasto no puede ser negativo';
  if (parsed > CAMPAIGN_LIMITS.gastoMax) {
    return `El gasto no puede pasar de ${formatNumber(CAMPAIGN_LIMITS.gastoMax)}`;
  }
  return undefined;
}

/** Moneda: código ISO de 3 letras (el backend lo pasa a mayúsculas). */
export function validateMoneda(value: string): string | undefined {
  const text = value.trim();
  if (text.length === 0) return undefined;
  if (!/^[A-Za-z]{3}$/.test(text)) return 'moneda: código ISO de 3 letras (ej. COP)';
  return undefined;
}

/**
 * Formulario de la fila → errores de formulario.
 *
 * El orden de los mensajes es el de los campos en pantalla y el `refine` del contrato («necesita
 * un id de Meta») cae en el campo que lo puede arreglar, no en el aviso general.
 */
export function validateCampaignForm(form: CampaignFormState): FormErrors {
  const porAnuncio = form.alcance === 'anuncio';

  return formErrorsFromFields({
    nombre: validateCampaignName(form.nombre),
    metaAdId: validateMetaId(form.metaAdId, 'El id del anuncio', porAnuncio),
    metaCampaignId: validateMetaId(form.metaCampaignId, 'El id de la campaña', !porAnuncio),
    gastoActual: validateGastoActual(form.gastoActual),
    moneda: validateMoneda(form.moneda),
  });
}

/** Formulario del diálogo de gasto → errores (el gasto es obligatorio ahí: se viene a cambiarlo). */
export function validateGastoForm(form: GastoFormState): FormErrors {
  const text = form.gastoActual.trim();

  return formErrorsFromFields({
    gastoActual:
      text.length === 0
        ? 'Escribe el gasto acumulado (0 si todavía no gastaste nada)'
        : validateGastoActual(text),
  });
}
